"use client";

import { useReducedMotion } from "framer-motion";
import * as React from "react";
import { z } from "zod";

import {
  IconNavArrowDown12Line,
  IconNavArrowRight12Line,
  IconNavArrowUp12Line,
} from "@/components/icons/iconoir";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { useLockBody } from "@/hooks/use-lock-body";
import { usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { MotionDiv } from "../animate";
import { Link } from "../ui/link";
import { resolveNavMegaMenu } from "./nav-mega-menu-data";
import { MobileNavigationDropdownItem } from "./navigation-dropdown-item";
import { navV2Schema } from "./types";
import { isMenuPathActive } from "./utils";

/** Figma side-menu container (5605:43029): white sheet + fixed 98px overlay. */
const MOBILE_NAV_OVERLAY_HEIGHT_PX = 98;

const sheetTransition = {
  type: "spring" as const,
  duration: 0.5,
  bounce: 0.18,
};

const backdropTransition = {
  duration: 0.35,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number],
};

export function MobileNav({
  menu,
  onItemPress = () => {},
  hasActionBar = false,
  locale,
}: z.infer<typeof navV2Schema> & {
  onItemPress?: () => void;
  isDark: boolean;
  hasActionBar?: boolean;
  locale?: string;
}) {
  useLockBody();
  const shouldReduceMotion = useReducedMotion();
  const [groupOpen, setGroupOpen] = React.useState(false);
  const pathname = usePathname();
  const isHome = pathname === "/";

  return (
    <MotionDiv
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={shouldReduceMotion ? { duration: 0.01 } : backdropTransition}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 flex flex-col overflow-hidden lg:hidden",
        hasActionBar ? "top-[127px] md:top-[91px]" : "top-[var(--nav-offset)]"
      )}
    >
      <MotionDiv
        initial={
          shouldReduceMotion ? false : { transform: "translateY(-100%)" }
        }
        animate={{ transform: "translateY(0px)" }}
        exit={
          shouldReduceMotion ? undefined : { transform: "translateY(-100%)" }
        }
        transition={shouldReduceMotion ? { duration: 0.01 } : sheetTransition}
        style={{ willChange: "transform" }}
        className="relative min-h-0 flex-1"
      >
        {/* Dim overlay fills the full sheet area so rounded corners reveal it. */}
        <div aria-hidden className="absolute inset-0 bg-black/30" />
        {/* Figma Container (5605:43101): white sheet with 16px bottom radius above 98px overlay. */}
        <div
          className="bg-grey-0 rounded-b-ds-xl absolute inset-x-0 top-0 z-[1] overflow-hidden"
          style={{ bottom: MOBILE_NAV_OVERLAY_HEIGHT_PX }}
        >
          <nav className="h-full overflow-y-auto">
            {menu && (
              <Accordion
                className="w-full"
                onValueChange={(value) => setGroupOpen(value.length > 0)}
              >
                {menu.map((menuItem) => {
                  if (menuItem._type === "navTextLink") {
                    return (
                      <MobileNavRow
                        key={menuItem.title}
                        href={menuItem.url}
                        external={menuItem.external ?? false}
                        isActive={
                          !groupOpen &&
                          !isHome &&
                          menuItem.url.includes(pathname)
                        }
                        onClick={onItemPress}
                      >
                        {menuItem.title}
                      </MobileNavRow>
                    );
                  }

                  const megaMenu = resolveNavMegaMenu(menuItem.title, locale);

                  if (megaMenu) {
                    return (
                      <AccordionItem
                        className="group/item data-open:border-grey-100 border-0 data-open:border-b"
                        value={menuItem.title}
                        key={menuItem.title}
                      >
                        <MobileAccordionTrigger
                          isActive={
                            !groupOpen &&
                            !isHome &&
                            isMenuPathActive(menuItem, pathname)
                          }
                        >
                          {menuItem.title}
                        </MobileAccordionTrigger>
                        <AccordionContent className="pt-2 pb-5">
                          <div className="flex flex-col gap-3 px-6">
                            {megaMenu.items.map((item) => (
                              <MobileNavigationDropdownItem
                                key={item.title}
                                item={item}
                                onClick={onItemPress}
                              />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  }

                  return (
                    <AccordionItem
                      className="group/item data-open:border-grey-100 border-0 data-open:border-b"
                      value={menuItem.title}
                      key={menuItem.title}
                    >
                      <MobileAccordionTrigger
                        isActive={
                          !groupOpen &&
                          !isHome &&
                          isMenuPathActive(menuItem, pathname)
                        }
                      >
                        {menuItem.title}
                      </MobileAccordionTrigger>
                      <AccordionContent className="pt-2 pb-5">
                        <div className="flex flex-col gap-3 px-6">
                          {menuItem.subMenu?.flatMap((subMenuItem) =>
                            subMenuItem.links.map((navLinkItem) => (
                              <Link
                                key={navLinkItem.title}
                                href={navLinkItem.url}
                                external={navLinkItem.external}
                                onClick={onItemPress}
                                className="text-ds-body-md text-grey-950"
                              >
                                {navLinkItem.title}
                              </Link>
                            ))
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </nav>
        </div>
      </MotionDiv>
    </MotionDiv>
  );
}

function MobileNavRow({
  children,
  href,
  external,
  isActive,
  onClick,
  className,
}: {
  children: React.ReactNode;
  href?: string;
  external?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  if (!href) {
    return (
      <div
        className={cn(
          "border-grey-100 flex items-center justify-between border-b px-6 py-5",
          className
        )}
      >
        <span
          className={cn(
            "text-ds-body-md text-grey-950",
            isActive && "font-medium"
          )}
        >
          {children}
        </span>
      </div>
    );
  }

  return (
    <Link
      href={href}
      external={external}
      onClick={onClick}
      className={cn(
        "border-grey-100 flex items-center justify-between border-b px-6 py-5",
        className
      )}
    >
      <span
        className={cn(
          "text-ds-body-md text-grey-950",
          isActive && "font-medium"
        )}
      >
        {children}
      </span>
      <span className="border-grey-100 bg-grey-0 flex size-8 items-center justify-center rounded-full border">
        <IconNavArrowRight12Line
          className="stroke-grey-950 size-3 stroke-[1.5px]"
          aria-hidden
        />
      </span>
    </Link>
  );
}

function MobileAccordionTrigger({
  children,
  isActive,
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  isActive?: boolean;
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        {...props}
        className={cn(
          "group border-grey-100 flex w-full items-center justify-between border-b px-6 py-5 group-data-open/item:border-b-0",
          className
        )}
      >
        <span
          className={cn(
            "text-ds-body-md text-grey-950",
            isActive && "font-medium"
          )}
        >
          {children}
        </span>
        <span className="border-grey-100 bg-grey-0 flex size-8 items-center justify-center rounded-full border">
          <IconNavArrowUp12Line
            className="stroke-grey-950 hidden size-3 stroke-[1.5px] group-data-panel-open:block"
            aria-hidden
          />
          <IconNavArrowDown12Line
            className="stroke-grey-950 size-3 stroke-[1.5px] group-data-panel-open:hidden"
            aria-hidden
          />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}
